export interface HelpFaqItem {
  question: string;
  answer: string;
}

export const helpFaq: HelpFaqItem[] = [
  {
    question: "How are my calories, macros and meals calculated?",
    answer: `**Your calorie target**

Your starting calorie target uses the [Mifflin–St Jeor Equation](https://reference.medscape.com/calculator/846/mifflin-st-jeor-equation) to estimate your resting energy needs from your age, sex, height and weight.

NutriCoach then considers your activity level and weight goal to estimate a daily calorie target. This is an estimate rather than a medical measurement, and it updates when the information in your profile changes.

**Your macro targets**

Your protein, carbohydrate and fat targets are calculated from your calorie target using guidance described in [Exercise and the Institute of Medicine recommendations for nutrition](https://pubmed.ncbi.nlm.nih.gov/16004827/).

NutriCoach works with these targets in the background, so you do not need to count calories or macros yourself. You can review and amend your targets in your profile.

**Your meal suggestions**

NutriCoach considers your food, dietary and lifestyle preferences when recommending meals that fit your calorie and macro requirements.

When meals are recommended or swapped, NutriCoach aims to keep your weekly calories and macros within approximately **±10% of your targets**.

These calculations and suggestions provide general wellness guidance. They are not medical advice and may not be suitable for everyone.
`,
  },
  {
    question: "How does NutriCoach work?",
    answer: `You tell me what you want to eat. It's that simple 😊

You can send me a voice note, type me a message, or take a picture of something you're thinking of having.

I'll check it against your calories, protein, sugar and the rest of your plan.

If it works, I'll tell you.

If it needs a tweak, I'll suggest a small adjustment.

If there's a better option, I'll show you something that keeps you closer to On Plan.

You can also tell me what you've already eaten.

I can then help work out what to have later, so your day and week still stay on track.

No calorie counting.

No macro tracking.

No guessing.

You tell me what you fancy, and I'll help you make it work 💜
`,
  },
  {
    question: "Do I need to count calories or macros?",
    answer: `No — I do that for you in the background 💜

Calories, protein, sugar and overall balance still matter.

But you don't need to count them yourself.

You tell me what you want to eat, and I'll check whether it fits your plan ✨

If it works, I'll tell you.

If it needs a small tweak, I'll suggest one.

If you've already had something, I can help adjust the rest of your day or week around it.

So instead of you having to think:

“Have I got enough calories left?”

“Have I had enough protein?”

“Is this too much?”

“Have I gone off plan?”

I'll do that thinking for you 🤓

No calorie counting.

No macro tracking.

No guessing.

You choose the food. I'll help keep you On Plan 💜
`,
  },
  {
    question: "What does On Plan actually mean?",
    answer: `I want you to view weight loss differently 💜

Not as all or nothing.

Not as “perfect” or “ruined”.

But as something realistic, flexible and built around real life.

That's why I use a **Flexible Deficit**.

Being On Plan means your food is still moving you towards your goal across the week.

Calories matter because they help create the deficit needed for fat loss.

Your target is usually around a **3,500 calorie deficit per week**.

That's the sweet spot.

But On Plan is not just about calories.

I'm also helping keep your protein, fibre, sugar and overall balance in a good place where possible ✨

So you're not just eating fewer calories.

You're eating in a way that supports your goal, keeps you feeling better and helps you stay consistent.

You don't have to hit everything perfectly.

You can be slightly under or slightly over and still be On Plan.

For example, if your week ends around:

**4,000 calorie deficit**

You're still On Plan.

**3,500 calorie deficit**

You're bang On Plan.

**2,500 calorie deficit**

You're still On Plan.

Why?

Because in all of those cases, you're still in a weekly deficit — which means you're still moving towards losing body fat.

And while I'm managing that, I'll also look at the quality of the week too.

Have you had enough protein?

Is fibre in a good place?

Has sugar been a bit high?

Does anything need balancing?

That's why one meal doesn't ruin everything.

A takeaway, chocolate, a meal out or a higher-calorie day can still fit.

I'll help you manage the day and week around it, so you can keep progressing without feeling like you've failed.

On Plan means progress across the bigger picture.

Not perfection.
`,
  },
  {
    question: "How do I know if I'm eating the right thing?",
    answer: `**I make sure you're eating the right thing.**

And if something isn't quite right, I'll tell you — then we'll make a small adjustment together 💜

You don't need to work it out alone.

When you choose a meal or ask for something, I'll look at how it fits your goal, your day and your week.

That means checking things like calories, protein, balance, hunger and whether the choice helps you stay On Plan.

Sometimes I might say:

**“That fits nicely.”**

Sometimes I might say:

**“That works, but it's a bit low in protein, so we'll make dinner stronger.”**

And sometimes I might say:

**“You can have it, but it may be worth keeping the rest of today simpler.”**

So it's not about finding the perfect food.

It's about knowing your choice fits the bigger picture ✨

You choose what you want.

I'll help make sure it works.
`,
  },
  {
    question: "What happens if I go over?",
    answer: `One higher meal or higher day does not ruin your progress 💜

I'll look at the bigger picture and check where you are across the day and week.

Sometimes nothing major needs changing.

Sometimes I might keep your next meal a little simpler.

Sometimes I might make tomorrow slightly lighter.

Sometimes I'll help balance the rest of the week so you stay On Plan.

The goal is not to punish you.

The goal is to keep you moving forward ✨

That's why I use a Flexible Deficit.

You don't need every day to be perfect.

You just need the overall week to still support your goal.

So if you go over, just tell me what you've had.

I'll work out what needs to happen next.

We resume.

We don't restart.

 major needs changing at all.

The goal is to resume, not restart.
`,
  },
  {
    question: "What should I ask you?",
    answer: `Basically anything you want!
You don't need to know the “right” question.

Just tell me what's going on 💜

You can ask me things like:

“What should I eat today?”**

**“Can I fit this in?”**

**“I've already had this — what should I have later?”**

**“Swap my dinner.”**

**“I'm hungry — what should I have?”**

**“I'm going out tonight, help me plan around it.”**

**“Why am I not losing weight” **

**“What does a deficit mean”**

**“Why do some people lose weight faster than others”**

**“I'm struggling this week, what can i do”**

**“I”m hungrier than normal”**

**“Its the time of the month and I'm craving chocolate”**

**“How am I doing this week?”**

**“I've gone over — what now?”**

**“Give me something quick from M&S.”**

**“Can I still have chocolate?”**

The more you tell me, the better I can guide you.

What you fancy, what you've already eaten, where you are, how hungry you feel, what your plans are or any questions about anything!

You can send me a voice note, type me a message, or take a picture.

I'm here to help you stay On Plan, one choice at a time.
`,
  },
];
